/**
 * Other Surface Save snapshot of visible prem/vol cells.
 * Uses the same rounding as the grid formatters (toFixed 2 / 3) without
 * importing generalHelper, which pulls a circular module graph that Jest cannot load.
 */

function toFixedOrEmpty(decimals, value) {
  if (value === null || value === undefined || value === "") return "";
  const number = parseFloat(value);
  if (Number.isNaN(number)) return "";
  return number.toFixed(decimals);
}

/** Vol cell text the trader sees, without the trailing %. */
export function displayedVolCellText(value) {
  if (value === null || value === undefined || value === "") return "";
  const raw = String(value).trim().replace(/%/g, "").trim();
  if (raw === "") return "";
  return toFixedOrEmpty(2, raw);
}

/** Premium cell text the trader sees (3dp). */
export function displayedPremiumCellText(value) {
  if (value === null || value === undefined || value === "") return "";
  return toFixedOrEmpty(3, value);
}

/**
 * Snapshot Other Surface grid rows as Save `displayed_points`.
 * Blank prem/vol rows are omitted; Save still sends the key as [].
 * `premium` and `vol` must already be the same server pair — do not pass a row
 * whose premium was edited locally while vol is still from an older payload.
 */
export function collectOtherSurfaceDisplayedPoints(table) {
  if (!Array.isArray(table)) return [];
  return table.reduce((points, row) => {
    if (row?.vol_smile_id == null) return points;
    const premium = displayedPremiumCellText(row.premium);
    const vol = displayedVolCellText(row.vol);
    if (premium === "" || vol === "") return points;
    points.push({
      vol_smile_id: row.vol_smile_id,
      tenor: row.tenor == null ? "" : String(row.tenor),
      underlying: row.underlying == null ? "" : String(row.underlying),
      strike: row.strike == null ? "" : String(row.strike),
      premium,
      vol,
    });
    return points;
  }, []);
}

/** Copy prem/vol primitives so a later grid edit cannot change the server pair. */
export function snapshotStrikePairs(table) {
  if (!Array.isArray(table)) return [];
  return table.map((row) => ({
    id: row?.id,
    vol_smile_id: row?.vol_smile_id,
    key: row?.key,
    tenor: row?.tenor,
    underlying: row?.underlying,
    strike: row?.strike,
    premium: row?.premium,
    vol: row?.vol,
  }));
}

export function patchKeepsPremVolTogether(row) {
  return row?.id != null && "premium" in row && "vol" in row;
}

/** Ladder refresh (set strikes, underlying reset, add/delete strike rows): replace whole tenors. */
export function shouldReplaceStickyStrikeTenorBlocks(patchRows) {
  if (!Array.isArray(patchRows) || patchRows.length === 0) return false;
  const smileCounts = new Map();
  for (const row of patchRows) {
    if (row?.vol_smile_id == null) continue;
    smileCounts.set(
      row.vol_smile_id,
      (smileCounts.get(row.vol_smile_id) ?? 0) + 1
    );
  }
  if ([...smileCounts.values()].some((count) => count > 1)) return true;
  return patchRows.some((row) => !patchKeepsPremVolTogether(row));
}

/**
 * Swap every row for each vol_smile_id present in `patchRows`, preserving table order.
 */
export function mergeStickyStrikeTableReplacingTenors(table, patchRows) {
  if (!Array.isArray(patchRows) || patchRows.length === 0) {
    return Array.isArray(table) ? table : [];
  }
  const blocks = new Map();
  for (const row of patchRows) {
    if (row?.vol_smile_id == null) continue;
    const block = blocks.get(row.vol_smile_id) ?? [];
    block.push(row);
    blocks.set(row.vol_smile_id, block);
  }
  if (blocks.size === 0) {
    return mergeStrikeRowsById(table, patchRows);
  }

  let result = [...(table ?? [])];
  for (const [volSmileId, newRows] of blocks) {
    const firstIndex = result.findIndex(
      (row) => row?.vol_smile_id === volSmileId
    );
    result = result.filter((row) => row?.vol_smile_id !== volSmileId);
    if (firstIndex === -1) {
      result.push(...newRows);
    } else {
      result.splice(firstIndex, 0, ...newRows);
    }
  }
  return result;
}

export function applyStickyStrikeRowPatch(table, patchRows) {
  if (shouldReplaceStickyStrikeTenorBlocks(patchRows)) {
    return mergeStickyStrikeTableReplacingTenors(table, patchRows);
  }
  return mergeStrikeRowsById(table, patchRows);
}

/**
 * Replace rows only when the patch carries premium and vol together.
 * A vol-only patch must not leave the previous premium beside the new vol.
 */
export function mergeStrikeRowsById(table, patchRows) {
  const patches = new Map();
  for (const row of patchRows ?? []) {
    if (!patchKeepsPremVolTogether(row)) continue;
    patches.set(String(row.id), row);
  }
  if (!Array.isArray(table)) return [...patches.values()];
  const seen = new Set();
  const merged = table.map((row) => {
    const id = String(row?.id);
    if (!patches.has(id)) return row;
    seen.add(id);
    return patches.get(id);
  });
  for (const [id, row] of patches) {
    if (!seen.has(id)) merged.push(row);
  }
  return merged;
}

/** Same pairing rule as mergeStrikeRowsById, stored as plain copies. */
export function committedPairsAfterPatch(previousPairs, patchRows) {
  const next = new Map(
    (previousPairs ?? []).map((row) => [String(row.id), row])
  );
  for (const row of patchRows ?? []) {
    if (!patchKeepsPremVolTogether(row)) continue;
    next.set(String(row.id), snapshotStrikePairs([row])[0]);
  }
  const ordered = [];
  const seen = new Set();
  for (const row of previousPairs ?? []) {
    const id = String(row?.id);
    if (!next.has(id) || seen.has(id)) continue;
    ordered.push(next.get(id));
    seen.add(id);
  }
  for (const [id, row] of next) {
    if (!seen.has(id)) ordered.push(row);
  }
  return ordered;
}

/**
 * Server pairs after a patch that replaces whole tenors. Only the patched tenors
 * change; other tenors keep their last server pair, because the live table may
 * hold a value typed into the grid (e.g. a UL still waiting for its response).
 */
export function committedPairsAfterTenorReplace(previousPairs, patchRows) {
  return mergeStickyStrikeTableReplacingTenors(
    previousPairs ?? [],
    snapshotStrikePairs(patchRows)
  );
}

/**
 * Premiums the grid is showing that are not the last server pair.
 * The edit value is the formatted cell (3dp), not a raw float from another snapshot.
 * Vol is intentionally not returned — the server response supplies the matching vol.
 */
export function premiumEditsBySmile(liveRows, committedPairs) {
  const committedById = new Map(
    (committedPairs ?? []).map((row) => [String(row.id), row])
  );
  const groups = new Map();
  if (!Array.isArray(liveRows)) return groups;
  for (const row of liveRows) {
    if (row?.vol_smile_id == null || row?.id == null || row?.key == null) {
      continue;
    }
    const committed = committedById.get(String(row.id));
    if (!committed) continue;
    const shown = displayedPremiumCellText(row.premium);
    const saved = displayedPremiumCellText(committed.premium);
    if (shown === saved) continue;
    const edits = groups.get(row.vol_smile_id) ?? [];
    edits.push({
      key: String(row.key),
      value: shown === "" ? null : Number(shown),
    });
    groups.set(row.vol_smile_id, edits);
  }
  return groups;
}

/**
 * Rows Save should post: server premium/vol, in grid order.
 * A live premium edit is ignored here; Save flushes that edit first.
 */
export function displayedRowsForSave(table, committedPairs) {
  const committed = Array.isArray(committedPairs) ? committedPairs : [];
  const byId = new Map(committed.map((row) => [String(row.id), row]));
  if (!Array.isArray(table) || table.length === 0) return committed;
  if (byId.size === 0) return snapshotStrikePairs(table);
  return table.map((row) => {
    const saved = byId.get(String(row?.id));
    return saved ?? snapshotStrikePairs([row])[0];
  });
}

/** Push the paired rows into the grid immediately, before React commits rowData. */
export function syncStickyStrikeGrid(api, table) {
  if (!api || api.destroyCalled || !Array.isArray(table)) return;
  api.setRowData(table);
}
